import { useDebounce } from 'shared/hooks';
import { dataForAddUserSearchTable } from 'shared/helpers/utils';
import { useLazyGetAdsEmployeesQuery } from 'app/services/edpApiV1/moderator';
import { useAppSelector } from 'app/store';
import { TableRowSelection } from 'antd/lib/table/interface';
import { Input, Pagination, Select, Spin, Table } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { FilterEmployees, NewsUserSearchType, TAddUserEmployee } from 'shared/types/news.types';
import SearchSettings from '../searchSettings/SearchSettings';
import style from './index.module.scss';

type TProps = {
  selectedUsers?: TAddUserEmployee[];
  addUsers: (users: TAddUserEmployee[]) => void;
};

const columns = [
  {
    title: 'ФИО',
    dataIndex: 'fio',
    key: 'fio',
  },
  {
    title: 'Табельный номер',
    dataIndex: 'personnelNumber',
    key: 'personnelNumber',
  },
  {
    title: 'Подразделение',
    dataIndex: 'division',
    key: 'division',
  },
];

const UsersSearch: FC<TProps> = ({ addUsers, selectedUsers }) => {
  const [getUsers, { data: result, isFetching }] = useLazyGetAdsEmployeesQuery();
  const [search, setSearch] = useState<string>('');
  const [userSearchType, setUserSearchType] = useState<NewsUserSearchType>(NewsUserSearchType.FIO);
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(10);
  const myOrganizationId = useAppSelector(state => state.entities.viewer.me.organization.id);
  const organizationId = useAppSelector(state => state.entities.viewer.me.organization.id);
  const debounceSearchValue = useDebounce<string>(search);
  const [searchOrganizationId, setSearchOrganizationId] = useState<string | null>(null);
  const [searchField, setSearchField] = useState<FilterEmployees>(FilterEmployees.EMPLOYEE);
  const [divisionId, setDivisionId] = useState<string | null>(null);

  const selectedRowKeys: number[] = useMemo(() => {
    if (selectedUsers) {
      return selectedUsers.map(elem => elem.id);
    }
    return [];
  }, [selectedUsers]);

  useEffect(() => {
    getUsers({
      isActive: true,
      page: page,
      size: size,
      personnelNumber: userSearchType === NewsUserSearchType.PERSONNEL_NUMBER ? debounceSearchValue : undefined,
      search: userSearchType === NewsUserSearchType.FIO ? debounceSearchValue : undefined,
      organizationId: searchOrganizationId ? searchOrganizationId : myOrganizationId,
      divisionId: divisionId ? divisionId : undefined,
    });
  }, [
    debounceSearchValue,
    organizationId,
    getUsers,
    page,
    size,
    divisionId,
    searchField,
    myOrganizationId,
    searchOrganizationId,
    userSearchType,
  ]);

  const rowSelection: TableRowSelection<TAddUserEmployee> = {
    type: 'checkbox',
    selectedRowKeys,
    onChange: (_: React.Key[], selectedRows: TAddUserEmployee[]) => {
      //Так как при каждом запросе на бэк таблица обновляется и параметр selectedRows становится пустым
      //Сравниваем данные в массиве selectedRows и в массиве, который пришел из бека
      const users = result?.data;

      if (!users) {
        return;
      }

      const pickedUsers: TAddUserEmployee[] = [];
      let newSelectedUsers = [...(selectedUsers || [])];

      for (const user of users) {
        let isEqual = false;
        for (const selectedUser of selectedRows) {
          if (user.id === selectedUser.id) {
            const userIsPicked = !!newSelectedUsers.find(elem => elem.id === user.id);
            //Кидаем новое значение, если оно есть в выбранных на этой странице, но нет в выбранных в общем
            if (!userIsPicked) {
              pickedUsers.push(selectedUser);
            }
            isEqual = true;
          }
        }
        // Если массив выбранных не содержит значение из массива с бека, удаляем его из выбранных
        if (!isEqual) {
          newSelectedUsers = newSelectedUsers.filter(elem => elem.id !== user.id);
        }
      }
      addUsers([...pickedUsers, ...(newSelectedUsers || [])]);
    },
  };

  const handlePagination = (value: number, pageSize: number) => {
    setPage(value);
    setSize(pageSize);
  };

  const onSearchOrganizationIdChanged = (data: string | null) => {
    setSearchOrganizationId(data);
    setPage(1);
  };

  const onSearchFieldChanged = (data: FilterEmployees) => {
    setSearchField(data);
    setPage(1);
  };

  return (
    <>
      <SearchSettings
        field={searchField}
        divisionId={divisionId}
        onDivisionIdChange={setDivisionId}
        organizationId={searchOrganizationId}
        onFieldChange={onSearchFieldChanged}
        onOrganizationIdChange={onSearchOrganizationIdChanged}
      />
      <div className={style.filter}>
        <Select
          style={{ width: 250 }}
          value={userSearchType}
          onChange={setUserSearchType}
          options={Object.keys(NewsUserSearchType).map(item => ({
            value: NewsUserSearchType[item],
          }))}
        />
        <Input placeholder="Поиск..." onChange={e => setSearch(e.target.value)} />
      </div>
      <Spin spinning={isFetching}>
        <Table
          rowSelection={rowSelection}
          pagination={false}
          columns={columns}
          dataSource={dataForAddUserSearchTable(result?.data || [])}
        />
        <Pagination
          showSizeChanger
          defaultCurrent={1}
          current={page}
          pageSize={size}
          total={result?.pagination.total}
          onChange={(value: number, pageSize: number) => handlePagination(value, pageSize)}
        />
      </Spin>
    </>
  );
};

export default UsersSearch;
